import intl from "react-intl-universal";
import { CurrencyIcon } from "../../../../../Components/Atoms/CurrencyIcon";
import { getBaasBalanceTotal } from "../../../../../Services/baasAccountsApi";
import {
  getBaasBankDisplayName,
  hasBaasBankingDetails,
} from "../../../../../Services/baasAccountsListApi";
import { formatMonetaryValue } from "../../../../../Utils/formatters";

const CURRENCY_ORDER = ["BRL", "USD", "EUR", "GBP", "USDC", "USDT"];
/** Stablecoin wallet assets — never show as fiat/account currency rails. */
const STABLES_WALLET_ONLY_CURRENCIES = new Set(["BRL1", "EURC"]);

const formatAmount = (value, currency) => formatMonetaryValue(value, currency);

const sortCurrencyViews = (views) =>
  [...views]
    .filter((view) => {
      const code = String(view.currency || "").toUpperCase();
      return code && !STABLES_WALLET_ONLY_CURRENCIES.has(code);
    })
    .sort((a, b) => {
    const codeA = String(a.currency || "").toUpperCase();
    const codeB = String(b.currency || "").toUpperCase();
    const indexA = CURRENCY_ORDER.indexOf(codeA);
    const indexB = CURRENCY_ORDER.indexOf(codeB);
    const rankA = indexA === -1 ? CURRENCY_ORDER.length : indexA;
    const rankB = indexB === -1 ? CURRENCY_ORDER.length : indexB;
    if (rankA !== rankB) return rankA - rankB;
    return codeA.localeCompare(codeB);
  });

const MetaStat = ({ label, value, emphasize = false, warn = false }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </p>
    <p
      className={`mt-0.5 break-words text-xs leading-snug ${
        warn
          ? "font-semibold text-amber-700 dark:text-amber-400"
          : emphasize
            ? "font-semibold text-brand-dark dark:text-gray-100"
            : "font-medium text-brand-dark dark:text-gray-200"
      }`}
    >
      {value}
    </p>
  </div>
);

const MetaLine = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">{label}</span>
      <span className="min-w-0 break-words text-right text-xs font-medium leading-snug text-brand-dark dark:text-gray-200">
        {value}
      </span>
    </div>
  );
};

/**
 * Compact highlight card: available balance first, banking details as a short footer.
 * Scheme is a badge — not a tall DetailRow stack that scrolled away above Extrato.
 */
const BaasAccountCurrencyCard = ({ view }) => {
  const currency = String(view.currency || "").toUpperCase();
  const available = Number(view.available ?? 0);
  const blocked = Number(view.blocked ?? 0);
  const future = Number(view.future ?? 0);
  const limit = Number(view.limit ?? 0);
  const showLimit = Boolean(view.allowOverdraft) || limit > 0;
  const total = getBaasBalanceTotal(view);
  const banking = view.banking ?? {};
  const bankingPresent = hasBaasBankingDetails(banking);
  const scheme = banking.scheme ? String(banking.scheme).trim() : "";
  // Banco = issuer bank name (e.g. JPMorgan Chase); never partner slug (paysmart).
  const bankName = getBaasBankDisplayName(banking);
  const partnerCode = banking.partnerCode || "";
  const bankCode =
    banking.bankCode && banking.bankCode !== partnerCode ? banking.bankCode : "";

  return (
    <article
      className="summary-card flex h-full min-h-[10.5rem] min-w-0 w-full flex-col !bg-white !p-4 dark:!border-brand-dark-border dark:!bg-brand-dark-elevated sm:!p-5"
      data-currency={currency || undefined}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="summary-card-icon">
            <CurrencyIcon asset={currency.toLowerCase()} size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {currency || "—"}
            </p>
            {view.countryCode ? (
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {view.countryCode}
              </p>
            ) : null}
          </div>
        </div>
        {scheme ? (
          <span className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600 dark:border-brand-dark-border dark:bg-[#121212] dark:text-gray-300">
            {scheme}
          </span>
        ) : null}
      </div>

      <div className="mt-4 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {intl.get("baas.processing.available")}
        </p>
        <p className="mt-1 break-words text-xl font-bold leading-tight text-brand-dark dark:text-gray-50">
          {formatAmount(available, currency || "BRL")}
        </p>
      </div>

      <div
        className={`mt-3 grid gap-x-3 gap-y-2 border-t border-gray-100 pt-3 dark:border-brand-dark-border ${
          showLimit ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"
        }`}
      >
        <MetaStat
          label={intl.get("baas.processing.blocked")}
          value={formatAmount(blocked, currency || "BRL")}
          warn={blocked > 0}
        />
        <MetaStat
          label={intl.get("baas.processing.future")}
          value={formatAmount(future, currency || "BRL")}
        />
        {showLimit ? (
          <MetaStat
            label={intl.get("baas.processing.overBalance") || intl.get("extract.overdraftLimit")}
            value={formatAmount(limit, currency || "BRL")}
          />
        ) : null}
        <MetaStat
          label={intl.get("baas.processing.total")}
          value={formatAmount(total, currency || "BRL")}
          emphasize
        />
      </div>

      <div className="mt-auto border-t border-gray-100 pt-3 dark:border-brand-dark-border">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {intl.get("baas.accounts.detail.banking.title")}
        </p>
        {bankingPresent ? (
          <div className="divide-y divide-gray-100 dark:divide-brand-dark-border">
            <MetaLine
              label={intl.get("baas.accounts.detail.banking.issuer")}
              value={bankName}
            />
            <MetaLine
              label={intl.get("baas.accounts.detail.banking.bank")}
              value={bankCode}
            />
            <MetaLine
              label={intl.get("baas.accounts.detail.banking.branch")}
              value={banking.branch}
            />
            <MetaLine
              label={intl.get("baas.accounts.detail.banking.accountNumber")}
              value={banking.accountNumber}
            />
            <MetaLine label={intl.get("baas.accounts.detail.banking.ispb")} value={banking.ispb} />
            <MetaLine
              label={intl.get("baas.accounts.detail.banking.routingNumber")}
              value={banking.routingNumber}
            />
            <MetaLine label={intl.get("baas.accounts.detail.banking.bic")} value={banking.bic} />
          </div>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {intl.get("baas.accounts.detail.banking.empty")}
          </p>
        )}
      </div>
    </article>
  );
};

/**
 * One highlight card per currency above Extrato/Limites tabs.
 * Always visible on account detail (not tab-scoped).
 */
export const BaasAccountCurrencyBalanceCards = ({
  currencyViews = [],
  loading = false,
}) => {
  const sorted = sortCurrencyViews(currencyViews);

  if (!sorted.length) {
    return (
      <div className="baas-currency-balance-panel">
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated">
          <p className="text-sm text-gray-500 dark:text-gray-300">
            {loading
              ? intl.get("baas.accounts.detail.balancesLoading")
              : intl.get("baas.processing.noBalances")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`baas-currency-balance-panel ${loading ? "opacity-80" : ""}`}
      aria-busy={loading || undefined}
    >
      <div className="baas-currency-balance-grid">
        {sorted.map((view) => (
          <BaasAccountCurrencyCard
            key={view.currency || view.countryCode || view.banking?.scheme}
            view={view}
          />
        ))}
      </div>
    </section>
  );
};
